// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides functionality to create new locations in the system.
 * Supports creating both basic locations and locations with full address details.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { LocationType, locationSchema } from '@/location/api/types';
import { LocationComponents } from '@/sparse/api/types';

/**
 * Schema for validating the API response when creating a new location.
 * Transforms the response to provide a cleaner interface with the created location data.
 */
const createLocationResponseSchema = z
  .object({
    data: locationSchema, // The created location data
    metadata: responseMetadataSchema, // Request tracking metadata
  })
  .transform(({ data, ...response }) => ({ ...response, location: data }));

type CreateLocationResponse = z.infer<typeof createLocationResponseSchema>;

/**
 * Union type for location creation parameters.
 * Supports two variants:
 * 1. Basic location with just name and type
 * 2. Full location with address and geographical details
 */
interface CreateLocationParameters {
  name: string; // Display name for the location
  type: LocationType; // Type of location (e.g., 'SITE', 'ZONE')
  customerId?: string | null; // Associated customer ID, sites only
  isInventory: boolean; // Whether this is an inventory location
  address?: LocationComponents | null;
  parentLocationId?: string; // Parent location ID (required for ZONE type)
  description?: string | null; // Location description
  consignee?: string | null; // Consignee customer ID
  isInventoryOverride?: boolean; // Zone inventory override
}

/**
 * Creates a new location in the system.
 * Supports creating both basic locations and locations with full address details.
 *
 * @param parameters - Configuration for the new location. Can be either basic or full details.
 * @returns Promise resolving to the created location data and request metadata
 * @throws Will throw an error if the location creation fails or if the response
 *         doesn't match the expected schema
 */
export async function createLocation(
  parameters: CreateLocationParameters
): Promise<CreateLocationResponse> {
  const url = '/v1/location';
  const { address, ...rest } = parameters;
  const body = {
    ...rest,
    address: address
      ? {
          addressLine1: address.addressLine1,
          addressLine2: address.addressLine2,
          city: address.locality,
          state: address.administrativeArea,
          postalCode: address.postalCode,
          country: address.countryCode,
          formattedAddress: address.formattedAddress,
        }
      : address,
  };
  const response = await api.post(url, body, {
    withCredentials: true,
  });

  return parseResponse(createLocationResponseSchema, response.data, {
    schemaName: 'CreateLocation',
    endpoint: url,
  });
}
